/**
 * Session Information Formatter (Frontend)
 * 
 * Frontend version of session info formatting utilities.
 * Mirrors the edge function version but works in browser context.
 * 
 * CRITICAL SECURITY RULES:
 * - NEVER expose raw IP addresses to users
 * - NEVER expose GPS coordinates to users
 * - Only show human-readable location (City, State, Country)
 * - Only show friendly browser/OS names
 */

/**
 * Parse user agent string into friendly components
 */
interface ParsedUserAgent {
  browser: string;
  os: string;
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
}

function parseBrowser(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('edg/')) return 'Microsoft Edge';
  if (ua.includes('opr/') || ua.includes('opera')) return 'Opera';
  if (ua.includes('brave')) return 'Brave';
  if (ua.includes('chrome/')) return 'Chrome';
  if (ua.includes('safari/') && !ua.includes('chrome')) return 'Safari';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('msie') || ua.includes('trident/')) return 'Internet Explorer';
  
  return 'Unknown Browser';
}

function parseOS(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('android')) return 'Android';
  if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS';
  if (ua.includes('windows nt 10.0')) return 'Windows 10/11';
  if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
  if (ua.includes('windows nt 6.2')) return 'Windows 8';
  if (ua.includes('windows nt 6.1')) return 'Windows 7';
  if (ua.includes('windows')) return 'Windows';
  if (ua.includes('mac os x')) return 'macOS';
  if (ua.includes('linux')) return 'Linux';
  if (ua.includes('ubuntu')) return 'Ubuntu';
  if (ua.includes('fedora')) return 'Fedora';
  
  return 'Unknown OS';
}

function parseDeviceType(userAgent: string): 'Mobile' | 'Tablet' | 'Desktop' {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('mobile') || ua.includes('iphone') || (ua.includes('android') && !ua.includes('tablet'))) {
    return 'Mobile';
  }
  
  if (ua.includes('tablet') || ua.includes('ipad')) {
    return 'Tablet';
  }
  
  return 'Desktop';
}

function parseUserAgent(userAgent: string): ParsedUserAgent {
  if (!userAgent || userAgent === 'unknown') {
    return {
      browser: 'Unknown Browser',
      os: 'Unknown OS',
      deviceType: 'Desktop',
    };
  }
  
  return {
    browser: parseBrowser(userAgent),
    os: parseOS(userAgent),
    deviceType: parseDeviceType(userAgent),
  };
}

/**
 * Session data from database queries
 */
export interface SessionData {
  ip_address?: string;
  user_agent?: string;
  device_fingerprint?: string;
  city?: string;
  state?: string;
  country?: string;
  created_at?: string;
  last_activity?: string;
}

/**
 * User-facing session info for display
 */
export interface DisplaySessionInfo {
  location: string;
  device: string;
  timestamp?: string;
  isCurrentDevice?: boolean;
}

/**
 * Format session data for user display
 * 
 * SECURITY: Sanitizes all technical details
 * 
 * @param session - Raw session data from database
 * @returns Sanitized info for display
 */
export function formatSessionForDisplay(session: SessionData): DisplaySessionInfo {
  // Format location (City, State, Country only - no IP)
  let location = "Unknown location";
  if (session.city || session.country) {
    const parts = [session.city, session.state, session.country].filter(Boolean);
    location = parts.join(", ");
  }
  
  // Format device (friendly browser + OS - no raw user agent)
  let device = "Unknown device";
  if (session.user_agent) {
    const parsed = parseUserAgent(session.user_agent);
    device = `${parsed.browser} on ${parsed.os}`;
    if (parsed.deviceType !== 'Desktop') {
      device += ` (${parsed.deviceType})`;
    }
  }
  
  // Format timestamp
  let timestamp: string | undefined;
  const timeValue = session.last_activity || session.created_at;
  if (timeValue) {
    timestamp = new Date(timeValue).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }
  
  return {
    location,
    device,
    timestamp,
  };
}

/**
 * Format location only (for cases where device info is separate)
 */
export function formatLocationForDisplay(session: Pick<SessionData, 'city' | 'state' | 'country'>): string {
  if (!session.city && !session.country) {
    return "Unknown location";
  }
  
  const parts = [session.city, session.state, session.country].filter(Boolean);
  return parts.join(", ");
}

/**
 * Format device info only
 */
export function formatDeviceForDisplay(userAgent?: string): string {
  if (!userAgent || userAgent === 'unknown') {
    return "Unknown device";
  }
  
  const parsed = parseUserAgent(userAgent);
  let device = `${parsed.browser} on ${parsed.os}`;
  
  if (parsed.deviceType !== 'Desktop') {
    device += ` (${parsed.deviceType})`;
  }
  
  return device;
}

/**
 * Get friendly browser name from user agent
 * (For backward compatibility with existing code)
 */
export function getBrowserInfo(userAgent: string): string {
  const parsed = parseUserAgent(userAgent);
  return parsed.browser;
}

/**
 * Get friendly OS name from user agent
 * (For backward compatibility with existing code)
 */
export function getOSInfo(userAgent: string): string {
  const parsed = parseUserAgent(userAgent);
  return parsed.os;
}

/**
 * Truncate session ID for display
 */
export function truncateSessionId(sessionId: string): string {
  if (sessionId.length <= 12) return sessionId;
  return `${sessionId.substring(0, 8)}...`;
}

/**
 * Check if a session is likely the current one
 * (Based on comparing user agents and timestamps)
 */
export function isCurrentSession(
  sessionUserAgent: string,
  sessionTimestamp: string
): boolean {
  // Check if user agents match
  const currentUA = navigator.userAgent;
  if (sessionUserAgent !== currentUA) {
    return false;
  }
  
  // Check if session is very recent (within last 5 minutes)
  const sessionTime = new Date(sessionTimestamp).getTime();
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;
  
  return (now - sessionTime) < fiveMinutes;
}
